import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Briefcase,
  GraduationCap,
  Award,
  Clock,
  Edit,
  Save,
  X,
  Camera,
  FileText,
  Target,
  TrendingUp
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import { UserDashboardLayout } from './UserDashboardLayout';
import toast from 'react-hot-toast';

interface UserProfileData {
  personal: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    address?: {
      street?: string;
      city?: string;
      province?: string;
      postalCode?: string;
      country?: string;
    };
    dateOfBirth?: string;
    avatar?: string;
  };
  professional: {
    employeeId?: string;
    designation?: string;
    department?: string;
    startDate?: string;
    manager?: string;
    skills?: string[];
    certifications?: Array<{
      name: string;
      issuer: string;
      date: string;
      expiryDate?: string;
    }>;
    education?: Array<{
      degree: string;
      institution: string;
      year: string;
    }>;
  };
  stats: {
    tasksCompleted: number;
    hoursWorked: number;
    projectsInvolved: number;
    utilizationRate: number;
  };
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}> = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-lg p-4 border border-gray-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

export const UserProfile: React.FC = () => {
  const { user } = useAuthStore();
  const [profileData, setProfileData] = useState<UserProfileData>({
    personal: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'Canada',
      },
      dateOfBirth: '',
    },
    professional: {
      employeeId: '',
      designation: '',
      department: '',
      startDate: '',
      manager: '',
      skills: [],
      certifications: [],
      education: [],
    },
    stats: {
      tasksCompleted: 0,
      hoursWorked: 0,
      projectsInvolved: 0,
      utilizationRate: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const [profileResponse, statsResponse] = await Promise.all([
        api.get('/user/profile'),
        api.get('/user/stats').catch(() => ({ data: { data: null } }))
      ]);

      if (profileResponse.data.success) {
        const profile = profileResponse.data.data;
        setProfileData(prev => ({
          ...prev,
          personal: {
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            email: profile.email || '',
            phone: profile.phone || '',
            address: profile.address || prev.personal.address,
            dateOfBirth: profile.dateOfBirth || '',
            avatar: profile.avatar || '',
          },
          professional: {
            employeeId: profile.employeeId || '',
            designation: profile.designation || '',
            department: profile.department || '',
            startDate: profile.startDate || '',
            manager: profile.manager || '',
            skills: profile.skills || [],
            certifications: profile.certifications || [],
            education: profile.education || [],
          },
        }));
      }

      if (statsResponse.data?.data) {
        setProfileData(prev => ({
          ...prev,
          stats: statsResponse.data.data,
        }));
      } else {
        // Mock stats for demonstration
        setProfileData(prev => ({
          ...prev,
          stats: {
            tasksCompleted: 47,
            hoursWorked: 156,
            projectsInvolved: 8,
            utilizationRate: 85,
          },
        }));
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/user/profile', {
        ...profileData.personal,
        ...profileData.professional,
      });
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (section: 'personal' | 'professional', field: string, value: any) => {
    setProfileData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleAddressChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      personal: {
        ...prev.personal,
        address: {
          ...prev.personal.address,
          [field]: value,
        },
      },
    }));
  };

  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">My Profile</h2>
              <p className="text-gray-600">
                Manage your personal and professional information.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {editing ? (
                <>
                  <button
                    onClick={() => setEditing(false)}
                    className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Profile Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center">
                {profileData.personal.avatar ? (
                  <img
                    src={profileData.personal.avatar}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-primary-600 font-bold text-2xl">
                    {profileData.personal.firstName.charAt(0)}{profileData.personal.lastName.charAt(0)}
                  </span>
                )}
              </div>
              {editing && (
                <button className="absolute bottom-0 right-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center hover:bg-primary-700">
                  <Camera className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900">
                {profileData.personal.firstName} {profileData.personal.lastName}
              </h3>
              <p className="text-lg text-gray-600 mb-2">{profileData.professional.designation}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-1" />
                  {profileData.personal.email}
                </div>
                {profileData.professional.department && (
                  <div className="flex items-center">
                    <Briefcase className="w-4 h-4 mr-1" />
                    {profileData.professional.department}
                  </div>
                )}
                {profileData.professional.startDate && (
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    Started {new Date(profileData.professional.startDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Tasks Completed"
            value={profileData.stats.tasksCompleted}
            icon={Target}
            color="bg-blue-500"
          />
          <StatCard
            title="Hours Worked"
            value={`${profileData.stats.hoursWorked}h`}
            icon={Clock}
            color="bg-green-500"
          />
          <StatCard
            title="Projects Involved"
            value={profileData.stats.projectsInvolved}
            icon={FileText}
            color="bg-purple-500"
          />
          <StatCard
            title="Utilization Rate"
            value={`${profileData.stats.utilizationRate}%`}
            icon={TrendingUp}
            color="bg-orange-500"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Personal Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  {editing ? (
                    <input
                      type="text"
                      value={profileData.personal.firstName}
                      onChange={(e) => handleInputChange('personal', 'firstName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  ) : (
                    <p className="text-gray-900">{profileData.personal.firstName || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  {editing ? (
                    <input
                      type="text"
                      value={profileData.personal.lastName}
                      onChange={(e) => handleInputChange('personal', 'lastName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  ) : (
                    <p className="text-gray-900">{profileData.personal.lastName || '-'}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="text-gray-900">{profileData.personal.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                {editing ? (
                  <input
                    type="tel"
                    value={profileData.personal.phone || ''}
                    onChange={(e) => handleInputChange('personal', 'phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.personal.phone || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                {editing ? (
                  <input
                    type="date"
                    value={profileData.personal.dateOfBirth || ''}
                    onChange={(e) => handleInputChange('personal', 'dateOfBirth', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <p className="text-gray-900">
                    {profileData.personal.dateOfBirth 
                      ? new Date(profileData.personal.dateOfBirth).toLocaleDateString()
                      : '-'
                    }
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Professional Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.professional.employeeId || ''}
                    onChange={(e) => handleInputChange('professional', 'employeeId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.professional.employeeId || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.professional.designation || ''}
                    onChange={(e) => handleInputChange('professional', 'designation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.professional.designation || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.professional.department || ''}
                    onChange={(e) => handleInputChange('professional', 'department', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.professional.department || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                {editing ? (
                  <input
                    type="date"
                    value={profileData.professional.startDate || ''}
                    onChange={(e) => handleInputChange('professional', 'startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <p className="text-gray-900">
                    {profileData.professional.startDate 
                      ? new Date(profileData.professional.startDate).toLocaleDateString()
                      : '-'
                    }
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.professional.manager || ''}
                    onChange={(e) => handleInputChange('professional', 'manager', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.professional.manager || '-'}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </UserDashboardLayout>
  );
};
